import type { TeamMember } from '@qlk/shared';
import { useMemo, useState } from 'react';

import { coQuyen, useToi } from '../../../lib/auth.js';
import {
  EmptyState,
  SegmentedControl,
  StatStrip,
  ToolbarBang,
  type OChiSo,
} from '../../../ui/index.js';
import {
  useNhom,
  useTaiKhoanGoogle,
  useThanhVien,
  useThanhVienLuuTru,
  useTomTatTeam,
  useVaiTro,
  type TaiKhoanGoogleDong,
} from '../api.js';
import { BangLuuTru } from './BangLuuTru.js';
import { BangTaiKhoanGoogle } from './BangTaiKhoanGoogle.js';
import { BangThanhVien } from './BangThanhVien.js';
import { DialogTaiKhoanGoogle } from './DialogTaiKhoanGoogle.js';
import { DialogThanhVien } from './DialogThanhVien.js';
import { MaTranQuyen } from './MaTranQuyen.js';

type Loc = 'dang-lam' | 'da-khoa' | 'luu-tru';

/** Kỳ tháng hiện tại — dùng để biết ai đang thuộc nhóm nào. */
function thangHienTai(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Tab "Thành viên" của trang Nhân sự & nhóm: danh sách người, ma trận phân
 * quyền và sổ tài khoản Google.
 *
 * Ba tập người (đang làm việc / đã khoá / đã lưu trữ) đi qua **một**
 * `SegmentedControl` thay vì hai tab lồng trong tab và một ô tích "hiện tài
 * khoản đã khoá": ba cách bày cùng một câu hỏi "xem ai" là ba chỗ người dùng
 * phải thử mới biết tài khoản mình tìm nằm đâu.
 */
export function TabThanhVien() {
  const toi = useToi();
  const laAdmin = coQuyen(toi.data?.permissions, 'team', 'admin');
  /*
   * Sửa ma trận phân quyền đòi `core:admin`, không phải `team:admin`: người
   * quản lý nhân sự mà sửa được ma trận là tự nâng quyền cho vai trò mình giữ
   * (rà soát 16/10/2026, mục V11). Server chặn, đây chỉ là ẩn ô chọn.
   */
  const laChuHeThong = coQuyen(toi.data?.permissions, 'core', 'admin');

  const thanhVien = useThanhVien();
  const tomTat = useTomTatTeam();
  const vaiTro = useVaiTro();
  const taiKhoan = useTaiKhoanGoogle();
  /* Cùng khoá truy vấn với tab Nhóm nên hai tab dùng chung một lượt gọi. */
  const nhom = useNhom(thangHienTai());

  const [loc, setLoc] = useState<Loc>('dang-lam');
  const luuTru = useThanhVienLuuTru(loc === 'luu-tru');

  const [dangSua, setDangSua] = useState<TeamMember | null>(null);
  const [suaTaiKhoan, setSuaTaiKhoan] = useState<TaiKhoanGoogleDong | null>(null);
  const [themTaiKhoan, setThemTaiKhoan] = useState(false);

  const tatCa = thanhVien.data ?? [];
  const daKhoa = tatCa.filter((t) => t.status === 'disabled');
  const dangLam = tatCa.filter((t) => t.status !== 'disabled');

  /** `userId` → tên các nhóm người đó đang ở trong. */
  const nhomTheoNguoi = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const n of nhom.data ?? []) {
      for (const tv of n.thanhVien) {
        map.set(tv.userId, [...(map.get(tv.userId) ?? []), n.name]);
      }
    }
    return map;
  }, [nhom.data]);

  /** Dự phòng khi không đọc được danh sách kênh của module 01. */
  const kenhDuPhong = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tatCa) for (const k of t.channels) map.set(k.id, k.name);
    for (const t of taiKhoan.data ?? []) {
      t.channelIds.forEach((id, i) => map.set(id, t.channelNames[i] ?? id));
    }
    return [...map].map(([id, name]) => ({ id, name }));
  }, [tatCa, taiKhoan.data]);

  const o: OChiSo[] = tomTat.data
    ? [
        {
          nhan: 'Thành viên',
          giaTri: tomTat.data.members,
          phu: `${String(tomTat.data.activeMembers)} đang hoạt động`,
        },
        {
          nhan: 'Lời mời chưa nhận',
          giaTri: tomTat.data.invited,
          phu: 'hết hạn sau 7 ngày',
        },
        { nhan: 'Tài khoản đã khoá', giaTri: tomTat.data.locked, phu: 'khoá tạm, mở lại được' },
        {
          nhan: 'Đã lưu trữ',
          giaTri: tomTat.data.archived,
          phu: 'đã nghỉ, không tính vào ô khác',
        },
        {
          nhan: 'Chưa bật 2FA',
          giaTri: tomTat.data.without2fa,
          phu: `${String(tomTat.data.googleAccountsWithout2fa)} tài khoản Google cũng chưa bật`,
        },
        {
          nhan: 'Quá hạn rà soát',
          giaTri: tomTat.data.accountsNeedingReview,
          phu: 'quá 90 ngày chưa kiểm tra',
        },
      ]
    : [];

  return (
    <div className="flex flex-col gap-4">
      {o.length > 0 && <StatStrip aria-label="Chỉ số nhân sự" o={o} />}

      <div>
        <ToolbarBang
          trai={
            <SegmentedControl
              aria-label="Tập thành viên"
              muc={[
                { giaTri: 'dang-lam', nhan: 'Đang làm việc', dem: dangLam.length },
                { giaTri: 'da-khoa', nhan: 'Đã khoá', dem: daKhoa.length },
                {
                  giaTri: 'luu-tru',
                  nhan: 'Đã lưu trữ',
                  ...(tomTat.data ? { dem: tomTat.data.archived } : {}),
                },
              ]}
              giaTri={loc}
              onChange={(v) => {
                setLoc(v as Loc);
              }}
            />
          }
        />

        {thanhVien.isError ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState
              loi
              title="Không tải được danh sách thành viên"
              description={thanhVien.error.message}
            />
          </div>
        ) : loc === 'luu-tru' ? (
          luuTru.isError ? (
            <div className="rounded-the border border-vien bg-be-mat">
              <EmptyState
                loi
                title="Không tải được danh sách lưu trữ"
                description={luuTru.error.message}
              />
            </div>
          ) : (
            <BangLuuTru dong={luuTru.data ?? []} dangTai={luuTru.isPending} />
          )
        ) : (
          <BangThanhVien
            thanhVien={loc === 'da-khoa' ? daKhoa : dangLam}
            nhomTheoNguoi={nhomTheoNguoi}
            dangTai={thanhVien.isPending}
            coSua={laAdmin}
            onSua={setDangSua}
          />
        )}
      </div>

      {/* `MaTranQuyen` tự dựng thẻ và tiêu đề của nó, nên **không** bọc thêm
          `KhoiMuc`: bọc vào là một khối có hai hàng tiêu đề chồng nhau. */}
      {vaiTro.isError ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không tải được ma trận quyền" description={vaiTro.error.message} />
        </div>
      ) : vaiTro.data && vaiTro.data.length > 0 ? (
        <MaTranQuyen
          vaiTro={vaiTro.data}
          coSua={laChuHeThong}
          vaiTroCuaToi={toi.data?.user.roles ?? []}
        />
      ) : (
        <div className="h-48 animate-pulse rounded-the border border-vien bg-be-mat" />
      )}

      {taiKhoan.isError ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không tải được sổ bảo mật" description={taiKhoan.error.message} />
        </div>
      ) : (
        <BangTaiKhoanGoogle
          dong={taiKhoan.data ?? []}
          dangTai={taiKhoan.isPending}
          coSua={laAdmin}
          onSua={setSuaTaiKhoan}
          onThem={() => {
            setThemTaiKhoan(true);
          }}
        />
      )}

      {dangSua && (
        <DialogThanhVien
          thanhVien={dangSua}
          kenhDuPhong={kenhDuPhong}
          onDong={() => {
            setDangSua(null);
          }}
        />
      )}

      {(suaTaiKhoan ?? themTaiKhoan) && (
        <DialogTaiKhoanGoogle
          {...(suaTaiKhoan ? { banGhi: suaTaiKhoan } : {})}
          kenhDuPhong={kenhDuPhong}
          onDong={() => {
            setSuaTaiKhoan(null);
            setThemTaiKhoan(false);
          }}
        />
      )}
    </div>
  );
}
